import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { fmtDate } from "@/lib/locale";
import { DOCUMENT_CATEGORIES, type DocumentCategory, type Lender, type FinancialStatement } from "@shared/schema";
import {
  exportPLPDF,
  exportBSPDF,
  buildPLDataFromStatement,
  buildBSDataFromStatement,
} from "@/lib/financialStatementPdf";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { uploadFileDirect } from "@/lib/directUpload";
import {
  Folder, FolderOpen, Search, Download, Eye, FileText, AlertTriangle,
  Upload, LayoutGrid, List, ChevronRight, ChevronLeft, ChevronDown,
  Loader2, X, Tag, Calendar, Plus, Trash2, CheckCircle2, ArrowUpDown, FileX,
} from "lucide-react";

function formatCurrency(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  return fmtDate(d);
}

type TFn = (key: string, opts?: any) => string;

function categoryLabel(category: string, t: TFn): string {
  return t(`documents:categories.${category}`, { defaultValue: category });
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    "Lender Statements": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    "Tax Returns / P&L": "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    "Bank Statements": "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400",
    "Floor Plan Agreements": "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
    "Vendor Agreements": "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    "Compliance / DMV": "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    "Insurance": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    "General Business": "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  };
  return colors[category] ?? "bg-muted text-muted-foreground";
}

function getCategoryIconStyle(category: string): { bg: string; icon: string; dot: string; folderBg: string; folderIcon: string } {
  const styles: Record<string, { bg: string; icon: string; dot: string; folderBg: string; folderIcon: string }> = {
    "Lender Statements":      { bg: "bg-blue-100 dark:bg-blue-900/40",   icon: "text-blue-600 dark:text-blue-400",   dot: "bg-blue-500",   folderBg: "bg-blue-50 dark:bg-blue-900/20",   folderIcon: "text-blue-500 dark:text-blue-400" },
    "Tax Returns / P&L":      { bg: "bg-green-100 dark:bg-green-900/40", icon: "text-green-600 dark:text-green-400", dot: "bg-green-500",  folderBg: "bg-green-50 dark:bg-green-900/20", folderIcon: "text-green-500 dark:text-green-400" },
    "Bank Statements":        { bg: "bg-teal-100 dark:bg-teal-900/40",   icon: "text-teal-600 dark:text-teal-400",   dot: "bg-teal-500",   folderBg: "bg-teal-50 dark:bg-teal-900/20",   folderIcon: "text-teal-500 dark:text-teal-400" },
    "Floor Plan Agreements":  { bg: "bg-purple-100 dark:bg-purple-900/40", icon: "text-purple-600 dark:text-purple-400", dot: "bg-purple-500", folderBg: "bg-purple-50 dark:bg-purple-900/20", folderIcon: "text-purple-500 dark:text-purple-400" },
    "Vendor Agreements":      { bg: "bg-orange-100 dark:bg-orange-900/40", icon: "text-orange-600 dark:text-orange-400", dot: "bg-orange-500", folderBg: "bg-orange-50 dark:bg-orange-900/20", folderIcon: "text-orange-500 dark:text-orange-400" },
    "Compliance / DMV":       { bg: "bg-red-100 dark:bg-red-900/40",     icon: "text-red-600 dark:text-red-400",     dot: "bg-red-500",    folderBg: "bg-red-50 dark:bg-red-900/20",     folderIcon: "text-red-500 dark:text-red-400" },
    "Insurance":              { bg: "bg-yellow-100 dark:bg-yellow-900/40", icon: "text-yellow-600 dark:text-yellow-400", dot: "bg-yellow-500", folderBg: "bg-yellow-50 dark:bg-yellow-900/20", folderIcon: "text-yellow-500 dark:text-yellow-400" },
    "General Business":       { bg: "bg-gray-100 dark:bg-gray-800/60",   icon: "text-gray-500 dark:text-gray-400",   dot: "bg-gray-400",   folderBg: "bg-gray-50 dark:bg-gray-800/40",   folderIcon: "text-gray-400 dark:text-gray-500" },
  };
  return styles[category] ?? { bg: "bg-muted", icon: "text-muted-foreground", dot: "bg-muted-foreground", folderBg: "bg-muted", folderIcon: "text-muted-foreground" };
}

type UnifiedDoc = {
  id: string;
  source: "parsed_statement" | "dealer_document" | "bank_statement" | "financial_statement";
  fileName: string;
  fileType: string;
  category: string;
  description: string | null;
  tags: string[];
  filePath: string | null;
  fileSize: number | null;
  uploadedAt: string | null;
  lenderName: string | null;
  lenderKey: string | null;
  statementPeriodStart: string | null;
  statementPeriodEnd: string | null;
  totalInterestPaid: string | null;
  totalFeesPaid: string | null;
  averageBalance: string | null;
  unitsSold: number | null;
  periodDays: number | null;
  avgDaysInStock: string | null;
  feeAdmin: string | null;
  feeAudit: string | null;
  feeCurtailment: string | null;
  feeDoc: string | null;
  feeTitle: string | null;
  feeOther: string | null;
  warningsJson: string | null;
  notes: string | null;
  status: string;
  fileExists: boolean | null;
};

async function downloadDocument(
  downloadUrl: string,
  fileName: string,
  toast: (opts: { title: string; description?: string; variant?: "default" | "destructive" }) => void,
  t: TFn,
) {
  try {
    const res = await fetch(downloadUrl, { credentials: "include" });
    if (!res.ok) {
      let message = t("documents:toast.fileCouldNotBeRetrievedRemoved");
      try {
        const json = await res.json();
        if (json?.message) message = json.message;
      } catch {}
      toast({ title: t("documents:toast.downloadFailed"), description: message, variant: "destructive" });
      return;
    }
    const contentType = res.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        const json = await res.json();
        toast({ title: t("documents:toast.downloadFailed"), description: json?.message ?? t("documents:toast.fileCouldNotBeRetrieved"), variant: "destructive" });
      } catch {
        toast({ title: t("documents:toast.downloadFailed"), description: t("documents:toast.fileCouldNotBeRetrievedRemoved"), variant: "destructive" });
      }
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch {
    toast({ title: t("documents:toast.downloadFailed"), description: t("documents:toast.fileCouldNotBeRetrievedRemoved"), variant: "destructive" });
  }
}

async function downloadFinancialStatementPdf(
  id: string,
  toast: (opts: { title: string; description?: string; variant?: "default" | "destructive" }) => void,
  t: TFn,
) {
  try {
    const res = await apiRequest("GET", `/api/financial-statements/${id}`);
    const statement = (await res.json()) as FinancialStatement;
    if ((statement.statementType ?? "pl") === "balance_sheet") {
      exportBSPDF(buildBSDataFromStatement(statement));
    } else {
      exportPLPDF(buildPLDataFromStatement(statement));
    }
  } catch {
    toast({ title: t("documents:toast.downloadFailed"), description: t("documents:toast.statementCouldNotBeGenerated"), variant: "destructive" });
  }
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 py-1.5">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

function DocumentDetailPanel({
  document,
  open,
  onClose,
}: {
  document: UnifiedDoc | null;
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { t } = useTranslation(["documents", "common"]);
  const isFinancialStatement = document?.source === "financial_statement";
  const { data: fullStatement } = useQuery<FinancialStatement>({
    queryKey: ["/api/financial-statements", document?.id],
    enabled: open && isFinancialStatement && !!document?.id,
  });
  if (!document) return null;

  const period =
    document.statementPeriodStart && document.statementPeriodEnd
      ? `${document.statementPeriodStart} – ${document.statementPeriodEnd}`
      : document.statementPeriodStart || document.statementPeriodEnd || null;

  const downloadUrl = document.source === "parsed_statement"
    ? `/api/documents/${document.id}/download`
    : document.source === "bank_statement"
      ? `/api/bank-statements/${document.id}/download`
      : document.source === "financial_statement"
        ? null
        : `/api/dealer-documents/${document.id}/download`;

  const handleDetailDownload = () => {
    if (isFinancialStatement) {
      if (!fullStatement) return;
      if ((fullStatement.statementType ?? "pl") === "balance_sheet") {
        exportBSPDF(buildBSDataFromStatement(fullStatement));
      } else {
        exportPLPDF(buildPLDataFromStatement(fullStatement));
      }
      return;
    }
    if (downloadUrl) downloadDocument(downloadUrl, document.fileName, toast, t);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="panel-document-detail">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-base leading-snug break-words">{document.fileName}</SheetTitle>
          <SheetDescription>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(document.category)}`}>
              {categoryLabel(document.category, t)}
            </span>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("documents:detail.documentInfo")}</p>
            <div className="divide-y divide-border">
              <DetailRow label={t("documents:detail.category")} value={categoryLabel(document.category, t)} />
              <DetailRow label={t("documents:detail.uploadDate")} value={formatDate(document.uploadedAt)} />
              <DetailRow label={t("documents:detail.fileSize")} value={formatFileSize(document.fileSize)} />
              <DetailRow label={t("documents:detail.fileType")} value={document.fileType} />
              {document.description && <DetailRow label={t("documents:detail.description")} value={document.description} />}
            </div>
          </div>

          {document.tags && document.tags.length > 0 && (
            <>
              <Separator />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("documents:detail.tags")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {document.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground" data-testid={`tag-${i}`}>
                      <Tag className="w-2.5 h-2.5" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {document.source === "parsed_statement" && (
            <>
              <Separator />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("documents:detail.statementDetails")}</p>
                <div className="divide-y divide-border">
                  <DetailRow label={t("documents:detail.lender")} value={document.lenderName} />
                  <DetailRow label={t("documents:detail.statementPeriod")} value={period} />
                  <DetailRow label={t("documents:detail.status")} value={
                    <Badge variant={document.status === "completed" ? "default" : document.status === "error" ? "destructive" : "secondary"}>
                      {document.status}
                    </Badge>
                  } />
                </div>
              </div>

              <Separator />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("documents:detail.extractedFinancials")}</p>
                <div className="divide-y divide-border">
                  <DetailRow label={t("documents:detail.totalInterestPaid")} value={formatCurrency(document.totalInterestPaid)} />
                  <DetailRow label={t("documents:detail.totalFeesPaid")} value={formatCurrency(document.totalFeesPaid)} />
                  <DetailRow label={t("documents:detail.averageBalance")} value={formatCurrency(document.averageBalance)} />
                  <DetailRow label={t("documents:detail.unitsSold")} value={document.unitsSold ?? "—"} />
                  <DetailRow label={t("documents:detail.periodDays")} value={document.periodDays ?? "—"} />
                  <DetailRow label={t("documents:detail.avgDaysInStock")} value={document.avgDaysInStock != null ? Number(document.avgDaysInStock).toFixed(1) : "—"} />
                </div>
              </div>

              <Separator />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("documents:detail.feeBreakdown")}</p>
                <div className="divide-y divide-border">
                  <DetailRow label={t("documents:detail.adminFee")} value={formatCurrency(document.feeAdmin)} />
                  <DetailRow label={t("documents:detail.auditFee")} value={formatCurrency(document.feeAudit)} />
                  <DetailRow label={t("documents:detail.curtailmentFee")} value={formatCurrency(document.feeCurtailment)} />
                  <DetailRow label={t("documents:detail.docFee")} value={formatCurrency(document.feeDoc)} />
                  <DetailRow label={t("documents:detail.titleFee")} value={formatCurrency(document.feeTitle)} />
                  <DetailRow label={t("documents:detail.otherFees")} value={formatCurrency(document.feeOther)} />
                </div>
              </div>
            </>
          )}

          {document.notes && (
            <>
              <Separator />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("documents:detail.notes")}</p>
                <p className="text-sm">{document.notes}</p>
              </div>
            </>
          )}

          <Separator />
          {document.fileExists === false && document.source !== "financial_statement" && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border text-sm text-muted-foreground" data-testid="badge-file-unavailable-detail">
              <FileX className="w-4 h-4 shrink-0 text-destructive/70" />
              <span>{t("documents:detail.fileUnavailableRemoved")}</span>
            </div>
          )}
          <Button
            className="w-full"
            variant="outline"
            onClick={handleDetailDownload}
            disabled={isFinancialStatement
              ? !fullStatement
              : (!document.filePath || !downloadUrl || document.fileExists === false)}
            title={!isFinancialStatement && document.fileExists === false ? t("documents:card.fileNoLongerAvailable") : undefined}
            data-testid="button-download-detail"
          >
            <Download className="w-4 h-4 mr-2" />
            {t("documents:detail.downloadFile")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

type UploadFlow = "simple" | "lender" | "bank" | "tax";

function getUploadFlow(category: string): UploadFlow {
  if (category === "Lender Statements") return "lender";
  if (category === "Bank Statements") return "bank";
  if (category === "Tax Returns / P&L") return "tax";
  return "simple";
}

function autoDetectCategory(filename: string): string | null {
  const name = filename.toLowerCase().replace(/[^a-z0-9]/g, " ");
  if (/\bbank\b|\btransaction|\bstatement.*bank|\bcash\b/.test(name)) return "Bank Statements";
  if (/\bfloor|lender|\breceivable|\bdealer.*statement|\bwholesale\b/.test(name)) return "Lender Statements";
  if (/\bp&l|\bpl\b|\bprofit.{0,10}loss|\bincome.{0,10}stat|\bfinancial.{0,10}stat|\btax.{0,10}return|\bform.{0,4}1120/.test(name)) return "Tax Returns / P&L";
  if (/\bbalance.{0,10}sheet|\bassets|\bliabilit/.test(name)) return "Tax Returns / P&L";
  if (/\binventory|\bvehicle.{0,10}list|\bstock.{0,10}list/.test(name)) return "General / Other";
  return null;
}

function UploadDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const { t } = useTranslation(["documents", "common"]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollLastProgressRef = useRef<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<string>("");
  const [autoDetected, setAutoDetected] = useState(false);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [dragging, setDragging] = useState(false);
  const [selectedLenderId, setSelectedLenderId] = useState<string>("");
  const [extractionStatus, setExtractionStatus] = useState<"idle" | "uploading" | "processing" | "done" | "error">("idle");
  const [progressText, setProgressText] = useState<string | null>(null);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [lenderExtractionResult, setLenderExtractionResult] = useState<any>(null);

  const flow = getUploadFlow(category);
  const isExtractionFlow = flow === "lender" || flow === "bank" || flow === "tax";
  const isWorking = extractionStatus === "uploading" || extractionStatus === "processing";

  const { data: lenders } = useQuery<Lender[]>({
    queryKey: ["/api/lenders"],
    enabled: flow === "lender",
  });

  useEffect(() => {
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    };
  }, []);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file || !category) throw new Error(t("documents:upload.fileAndCategoryRequired"));
      const { uploadId } = await uploadFileDirect(file, "dealer_document");
      const res = await fetch("/api/dealer-documents/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId, category, description: description || undefined, tags: tags || undefined }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: t("documents:upload.uploadFailed") }));
        throw new Error(err.message || t("documents:upload.uploadFailed"));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      toast({ title: t("documents:toast.documentUploaded"), description: t("documents:toast.documentUploadedDescription", { name: file?.name }) });
      onSuccess();
      resetForm();
    },
    onError: (err: Error) => {
      toast({ title: t("documents:toast.uploadFailed"), description: err.message, variant: "destructive" });
    },
  });

  function pickFile(f: File) {
    setFile(f);
    const detected = autoDetectCategory(f.name);
    if (detected) {
      setCategory(detected);
      setAutoDetected(true);
    } else {
      setAutoDetected(false);
    }
    setSelectedLenderId("");
    setExtractionStatus("idle");
    setExtractionError(null);
  }

  function resetForm() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    pollLastProgressRef.current = null;
    setFile(null);
    setCategory("");
    setAutoDetected(false);
    setDescription("");
    setTags("");
    setSelectedLenderId("");
    setExtractionStatus("idle");
    setProgressText(null);
    setExtractionError(null);
    setLenderExtractionResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleClose() {
    if (uploadMutation.isPending || isWorking) return;
    resetForm();
    onClose();
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) pickFile(f);
  }

  async function handleLenderUpload() {
    if (!file) return;
    setExtractionStatus("uploading");
    setProgressText(t("documents:upload.uploadingFile"));
    setExtractionError(null);

    let uploadRes: Response;
    try {
      const { uploadId } = await uploadFileDirect(file, "floorplan");
      uploadRes = await fetch("/api/parse-floorplan/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId, lenderId: selectedLenderId || undefined }),
        credentials: "include",
      });
    } catch {
      setExtractionStatus("error");
      setExtractionError(t("documents:upload.networkError"));
      return;
    }

    if (!uploadRes.ok) {
      const body = await uploadRes.json().catch(() => ({ message: t("documents:upload.uploadFailed") }));
      setExtractionStatus("error");
      setExtractionError(body.message || t("documents:upload.uploadFailed"));
      return;
    }

    const uploadResJson = await uploadRes.json();

    if (uploadResJson.deduplicated && uploadResJson.statement && !uploadResJson.jobId) {
      const stmt = uploadResJson.statement;
      const dedupResult = {
        statement: stmt,
        parsed: {
          lenderName: stmt.lenderName ?? "",
          lenderKey: stmt.lenderKey ?? "generic",
          statementPeriod: { start: stmt.statementPeriodStart, end: stmt.statementPeriodEnd },
          totals: {
            totalInterestPaid: parseFloat(stmt.totalInterestPaid || "0"),
            totalFeesPaid: parseFloat(stmt.totalFeesPaid || "0"),
            averageBalance: parseFloat(stmt.averageBalance || "0"),
            unitsSold: stmt.unitsSold ?? 0,
            periodDays: stmt.periodDays ?? 30,
            avgDaysInStock: parseFloat(stmt.avgDaysInStock || "0"),
          },
          feeBreakdown: {
            feeAdmin: parseFloat(stmt.feeAdmin || "0"),
            feeAudit: parseFloat(stmt.feeAudit || "0"),
            feeCurtailment: parseFloat(stmt.feeCurtailment || "0"),
            feeDoc: parseFloat(stmt.feeDoc || "0"),
            feeTitle: parseFloat(stmt.feeTitle || "0"),
            feeOther: parseFloat(stmt.feeOther || "0"),
          },
          receivables: JSON.parse(stmt.receivablesJson || "[]"),
          detectedFieldsConfidence: JSON.parse(stmt.confidenceJson || "{}"),
          warnings: JSON.parse(stmt.warningsJson || "[]"),
        },
        extractionMethod: stmt.extractionMethod ?? "cached",
        success: true,
      };
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      setLenderExtractionResult(dedupResult);
      setExtractionStatus("done");
      return;
    }

    const { jobId } = uploadResJson;
    setExtractionStatus("processing");
    setProgressText(t("documents:upload.extractingStatementData"));

    const POLL_TIMEOUT_MS = 8 * 60 * 1000;
    pollLastProgressRef.current = null;

    const startDeadline = () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        pollTimeoutRef.current = null;
        setExtractionStatus("error");
        setExtractionError(t("documents:upload.extractionTakingLonger"));
      }, POLL_TIMEOUT_MS);
    };
    startDeadline();

    pollRef.current = setInterval(async () => {
      try {
        const statusRes = await fetch(`/api/parse-floorplan/status/${jobId}`, { credentials: "include" });
        const statusJson = await statusRes.json();
        if (statusJson.status === "done") {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
          queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
          queryClient.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
          setLenderExtractionResult(statusJson.result ?? null);
          setExtractionStatus("done");
        } else if (statusJson.status === "error") {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
          setExtractionStatus("error");
          setExtractionError(statusJson.error || t("documents:upload.extractionFailedRetry"));
        } else if (statusJson.status === "processing") {
          const token = `${statusJson.step ?? ""}|${statusJson.pct ?? ""}`;
          if (token !== pollLastProgressRef.current) {
            pollLastProgressRef.current = token;
            startDeadline();
          }
        }
      } catch {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
        setExtractionStatus("error");
        setExtractionError(t("documents:upload.lostConnection"));
      }
    }, 2000);
  }

  async function handleBankUpload() {
    if (!file) return;
    setExtractionStatus("uploading");
    setProgressText(t("documents:upload.uploadingFile"));
    setExtractionError(null);

    let uploadRes: Response;
    try {
      const { uploadId } = await uploadFileDirect(file, "bank_statement");
      uploadRes = await fetch("/api/bank-statements/upload/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId }),
        credentials: "include",
      });
    } catch {
      setExtractionStatus("error");
      setExtractionError(t("documents:upload.networkError"));
      return;
    }

    if (!uploadRes.ok) {
      const body = await uploadRes.json().catch(() => ({ message: t("documents:upload.uploadFailed") }));
      setExtractionStatus("error");
      setExtractionError(body.message || t("documents:upload.uploadFailed"));
      return;
    }

    const { jobId } = await uploadRes.json();
    setExtractionStatus("processing");
    setProgressText(t("documents:upload.aiExtractingBankData"));

    const POLL_TIMEOUT_MS = 8 * 60 * 1000;
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    pollTimeoutRef.current = setTimeout(() => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      pollTimeoutRef.current = null;
      setExtractionStatus("error");
      setExtractionError(t("documents:upload.extractionTakingLonger"));
    }, POLL_TIMEOUT_MS);

    pollRef.current = setInterval(async () => {
      try {
        const statusRes = await fetch(`/api/bank-statements/upload/status/${jobId}`, { credentials: "include" });
        const statusJson = await statusRes.json();
        if (statusJson.status === "done") {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
          queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
          queryClient.invalidateQueries({ queryKey: ["/api/bank-statements"] });
          setExtractionStatus("done");
        } else if (statusJson.status === "error") {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
          setExtractionStatus("error");
          setExtractionError(statusJson.error || t("documents:upload.extractionFailedRetry"));
        }
      } catch {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
        setExtractionStatus("error");
        setExtractionError(t("documents:upload.lostConnection"));
      }
    }, 2000);
  }

  async function handleTaxUpload() {
    if (!file) return;
    setExtractionStatus("uploading");
    setProgressText(t("documents:upload.uploadingFile"));
    setExtractionError(null);

    let uploadRes: Response;
    try {
      const { uploadId } = await uploadFileDirect(file, "financial_statement");
      uploadRes = await fetch("/api/financial-statements/upload/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId }),
        credentials: "include",
      });
    } catch {
      setExtractionStatus("error");
      setExtractionError(t("documents:upload.networkError"));
      return;
    }

    if (!uploadRes.ok) {
      const body = await uploadRes.json().catch(() => ({ message: t("documents:upload.uploadFailed") }));
      setExtractionStatus("error");
      setExtractionError(body.message || t("documents:upload.uploadFailed"));
      return;
    }

    const { jobId } = await uploadRes.json();
    setExtractionStatus("processing");
    setProgressText(t("documents:upload.aiExtractingFinancialData"));

    const POLL_TIMEOUT_MS = 8 * 60 * 1000;
    pollLastProgressRef.current = null;

    const startDeadline = () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        pollTimeoutRef.current = null;
        setExtractionStatus("error");
        setExtractionError(t("documents:upload.extractionTakingLonger"));
      }, POLL_TIMEOUT_MS);
    };
    startDeadline();

    pollRef.current = setInterval(async () => {
      try {
        const statusRes = await fetch(`/api/financial-statements/upload/status/${jobId}`, { credentials: "include" });
        const statusJson = await statusRes.json();
        if (statusJson.status === "done" || statusJson.statementId) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
          queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
          queryClient.invalidateQueries({ queryKey: ["/api/financial-statements"] });
          setExtractionStatus("done");
        } else if (statusJson.status === "error") {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
          setExtractionStatus("error");
          setExtractionError(statusJson.error || t("documents:upload.extractionFailedRetry"));
        } else if (statusJson.status === "processing") {
          const p = statusJson.progress as { step?: string; pct?: number } | null;
          const token = `${p?.step ?? ""}|${p?.pct ?? ""}`;
          if (token !== pollLastProgressRef.current) {
            pollLastProgressRef.current = token;
            startDeadline();
          }
        }
      } catch {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
        setExtractionStatus("error");
        setExtractionError(t("documents:upload.lostConnection"));
      }
    }, 2000);
  }

  function handleSubmit() {
    if (flow === "lender") handleLenderUpload();
    else if (flow === "bank") handleBankUpload();
    else if (flow === "tax") handleTaxUpload();
    else uploadMutation.mutate();
  }

  const isPending = uploadMutation.isPending || isWorking;
  const isSubmitDisabled = !file || !category || isPending || (flow === "lender" && !selectedLenderId);

  function handleDone() {
    resetForm();
    onSuccess();
  }

  const dialogTitle =
    flow === "lender" ? t("documents:upload.titleLender")
    : flow === "bank" ? t("documents:upload.titleBank")
    : flow === "tax" ? t("documents:upload.titleTax")
    : t("documents:upload.titleDefault");

  const selectedLenderName = (lenders ?? []).find((l) => l.id === selectedLenderId)?.name ?? t("documents:upload.lenderFallback");

  const fmt$ = (v: number | null | undefined, d = 0) =>
    v == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: d, maximumFractionDigits: d }).format(v);
  const num = (v: any, def = 0) => v != null ? parseFloat(v) : def;

  function LenderExtractionSummary({ result }: { result: any }) {
    const p = result.parsed;
    const warnings: string[] = p.warnings ?? [];
    const receivables: any[] = p.receivables ?? [];
    const totalInterest = num(p.totals.totalInterestPaid);
    const totalFees = num(p.totals.totalFeesPaid);
    const avgBalance = num(p.totals.averageBalance);
    const unitsSold = p.totals.unitsSold || 0;
    const qualifyingRows = receivables.filter(
      (r: any) => (r.principal ?? 0) > 0 && (r.daysOutstanding ?? 0) > 0 && ((r.interest ?? 0) + (r.fees ?? 0)) > 0
    );
    const effectiveUnitCount = unitsSold > 0 ? unitsSold : qualifyingRows.length > 0 ? qualifyingRows.length : receivables.length;
    const totalCollateralFees = receivables.reduce((s: number, r: any) => s + (r.collateralFee ?? 0), 0);
    const avgInterestPerUnit = qualifyingRows.length > 0
      ? qualifyingRows.reduce((s: number, r: any) => s + (r.interest ?? 0), 0) / qualifyingRows.length
      : effectiveUnitCount > 0 ? totalInterest / effectiveUnitCount : null;
    const avgFeesPerUnit = qualifyingRows.length > 0
      ? qualifyingRows.reduce((s: number, r: any) => s + (r.fees ?? 0) - (r.collateralFee ?? 0), 0) / qualifyingRows.length
      : effectiveUnitCount > 0 ? (totalFees - totalCollateralFees) / effectiveUnitCount : null;
    const portfolioApr = qualifyingRows.length > 0
      ? (qualifyingRows.reduce((s: number, r: any) => s + (r.interest ?? 0) + (r.fees ?? 0), 0) /
         qualifyingRows.reduce((s: number, r: any) => s + (r.principal ?? 0) * (r.daysOutstanding ?? 0), 0)) * 365 * 100
      : null;
    const avgDaysFloored = num(p.totals.avgDaysInStock);
    const annualDivisor = avgDaysFloored > 0 ? avgDaysFloored : (p.totals.periodDays || 30);
    const statementApr = avgBalance > 0 && (totalInterest > 0 || totalFees > 0)
      ? ((totalInterest + totalFees) / avgBalance) * (365 / annualDivisor) * 100
      : null;
    const effectiveApr = portfolioApr ?? statementApr;
    const ac = p.accuracyCheck as any;
    const acStatus = ac?.status ?? "unverified";
    const acColor = acStatus === "verified" || acStatus === "ai-corrected"
      ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-400"
      : acStatus === "warning"
        ? "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-950/30 dark:border-yellow-800 dark:text-yellow-400"
        : acStatus === "needs-review"
          ? "bg-destructive/10 border-destructive/30 text-destructive"
          : "bg-muted/40 border-border text-muted-foreground";
    const acLabel = acStatus === "verified" ? t("documents:summary.acVerified")
      : acStatus === "ai-corrected" ? t("documents:summary.acAiCorrected")
      : acStatus === "warning" ? t("documents:summary.acWarning")
      : acStatus === "needs-review" ? t("documents:summary.acNeedsReview")
      : t("documents:summary.acUnverified");

    return (
      <div className="space-y-3" data-testid="lender-extraction-summary">
        <div className="flex items-center gap-2 flex-wrap">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          <span className="text-sm font-medium">{t("documents:summary.statementParsedFor", { lender: selectedLenderName })}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2.5 rounded-lg bg-primary/8 border border-primary/20 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t("documents:summary.avgInterestPerUnit")}</p>
            <p className="text-base font-bold text-primary" data-testid="result-avg-interest">{avgInterestPerUnit !== null ? fmt$(avgInterestPerUnit) : "—"}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/40 border text-center">
            <p className="text-xs text-muted-foreground mb-1">{t("documents:summary.avgFeesPerUnit")}</p>
            <p className="text-base font-bold" data-testid="result-avg-fees">{avgFeesPerUnit !== null ? fmt$(avgFeesPerUnit) : "—"}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/40 border text-center">
            <p className="text-xs text-muted-foreground mb-1">{t("documents:summary.effectiveRate")}</p>
            <p className="text-base font-bold" data-testid="result-effective-apr">{effectiveApr !== null ? `${effectiveApr.toFixed(2)}%` : "—"}</p>
          </div>
        </div>
        <div className="rounded-lg border text-xs overflow-hidden" data-testid="extraction-summary">
          <div className="px-3 py-1.5 bg-muted/20 border-b">
            <span className="uppercase tracking-wide text-[10px] text-muted-foreground font-semibold">{t("documents:summary.aiExtractionSummary")}</span>
          </div>
          <div className="px-3 py-2 space-y-2">
            <div className="grid grid-cols-4 gap-1.5">
              <div className="p-1.5 rounded bg-muted/30 text-center">
                <p className="text-muted-foreground mb-0.5">{t("documents:summary.vehicles")}</p>
                <p className="font-semibold">{ac?.vehicleCount ?? p.totals?.unitsSold ?? "—"}</p>
              </div>
              <div className="p-1.5 rounded bg-muted/30 text-center">
                <p className="text-muted-foreground mb-0.5">{t("documents:summary.interest")}</p>
                <p className="font-semibold">{totalInterest > 0 ? fmt$(totalInterest) : "—"}</p>
              </div>
              <div className="p-1.5 rounded bg-muted/30 text-center">
                <p className="text-muted-foreground mb-0.5">{t("documents:summary.fees")}</p>
                <p className="font-semibold">{totalFees > 0 ? fmt$(totalFees) : "—"}</p>
              </div>
              <div className="p-1.5 rounded bg-muted/30 text-center">
                <p className="text-muted-foreground mb-0.5">{t("documents:summary.rate")}</p>
                <p className="font-semibold">{effectiveApr !== null ? `${effectiveApr.toFixed(1)}%` : "—"}</p>
              </div>
            </div>
            <div className={`flex items-start gap-2 px-2 py-1.5 rounded border ${acColor}`}>
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>{acLabel}{ac?.note ? ` — ${ac.note}` : ""}</span>
            </div>
          </div>
        </div>
        {warnings.length > 0 && (
          <div className="space-y-1">
            {warnings.map((w: string, i: number) => (
              <div key={i} className="flex gap-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{w}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t("documents:summary.statementSavedNote")}</p>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className={extractionStatus === "done" && flow === "lender" && lenderExtractionResult ? "sm:max-w-lg" : "sm:max-w-md"} data-testid="dialog-upload-document">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {extractionStatus === "done" ? (
            flow === "lender" && lenderExtractionResult ? (
              <LenderExtractionSummary result={lenderExtractionResult} />
            ) : (
              <div className="flex flex-col items-center justify-center py-6 gap-3" data-testid="upload-done-state">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-sm">{t("documents:upload.processedSuccessfully")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("documents:upload.processedSuccessfullyDescription")}</p>
                </div>
              </div>
            )
          ) : isWorking ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3" data-testid="upload-processing-state">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm font-medium text-center">{progressText ?? t("documents:upload.processing")}</p>
              <p className="text-xs text-muted-foreground text-center">{t("documents:upload.mayTakeMinute")}</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="upload-category">{t("documents:upload.category")} <span className="text-destructive">*</span></Label>
                  {autoDetected && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20" data-testid="badge-auto-detected">
                      {t("documents:upload.autoDetected")}
                    </span>
                  )}
                </div>
                <Select value={category} onValueChange={(v) => { setCategory(v); setAutoDetected(false); setSelectedLenderId(""); setExtractionStatus("idle"); setExtractionError(null); }}>
                  <SelectTrigger id="upload-category" data-testid="select-upload-category">
                    <SelectValue placeholder={t("documents:upload.selectCategory")} />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{categoryLabel(cat, t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {flow === "lender" && (
                <div className="space-y-1.5">
                  <Label htmlFor="upload-lender">{t("documents:upload.lender")} <span className="text-destructive">*</span></Label>
                  <Select value={selectedLenderId} onValueChange={setSelectedLenderId}>
                    <SelectTrigger id="upload-lender" data-testid="select-upload-lender">
                      <SelectValue placeholder={t("documents:upload.selectLender")} />
                    </SelectTrigger>
                    <SelectContent>
                      {(lenders ?? []).map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{t("documents:upload.lenderHint")}</p>
                </div>
              )}

              {flow === "bank" && (
                <div className="flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/15 px-3 py-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t("documents:upload.bankHint")}
                  </p>
                </div>
              )}

              {flow === "tax" && (
                <div className="flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/15 px-3 py-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t("documents:upload.taxHintPrefix")}<a href="/financial-statements" className="text-primary hover:underline">{t("documents:upload.taxHintLink")}</a>{t("documents:upload.taxHintSuffix")}
                  </p>
                </div>
              )}

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                data-testid="dropzone-upload"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={isExtractionFlow ? ".pdf" : undefined}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) pickFile(f); }}
                  data-testid="input-file-upload"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-6 h-6 text-primary" />
                    <div className="text-left">
                      <p className="font-medium text-sm truncate max-w-xs" data-testid="text-selected-filename">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="ml-auto shrink-0"
                      onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      data-testid="button-clear-file"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm font-medium">{t("documents:upload.dropFile")}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isExtractionFlow ? t("documents:upload.pdfOnly") : t("documents:upload.anyFileType")}
                    </p>
                  </div>
                )}
              </div>

              {!isExtractionFlow && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="upload-description">{t("documents:upload.descriptionLabel")} <span className="text-muted-foreground text-xs">{t("documents:upload.optional")}</span></Label>
                    <Textarea
                      id="upload-description"
                      placeholder={t("documents:upload.descriptionPlaceholder")}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      data-testid="input-upload-description"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="upload-tags">{t("documents:upload.tagsLabel")} <span className="text-muted-foreground text-xs">{t("documents:upload.optionalCommaSeparated")}</span></Label>
                    <Input
                      id="upload-tags"
                      placeholder={t("documents:upload.tagsPlaceholder")}
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      data-testid="input-upload-tags"
                    />
                  </div>
                </>
              )}

              {extractionStatus === "error" && extractionError && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="upload-error">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  {extractionError}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {extractionStatus === "done" ? (
            <Button onClick={handleDone} data-testid="button-upload-done">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t("documents:upload.done")}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isPending} data-testid="button-cancel-upload">
                {t("documents:upload.cancel")}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                data-testid="button-submit-upload"
              >
                {isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {isExtractionFlow ? (isPending ? t("documents:upload.processing") : t("documents:upload.uploadAndExtract")) : t("documents:upload.upload")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FolderTile({
  category,
  count,
  onClick,
}: {
  category: string;
  count: number;
  onClick: () => void;
}) {
  const { t } = useTranslation(["documents", "common"]);
  const style = getCategoryIconStyle(category);
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-start gap-3 p-4 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-md transition-all duration-150 text-left w-full"
      data-testid={`folder-tile-${category.replace(/[\s/&]+/g, "-").toLowerCase()}`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${style.folderBg} group-hover:scale-105 transition-transform duration-150`}>
        <Folder className={`w-6 h-6 ${style.folderIcon}`} strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0 w-full">
        <p className="font-semibold text-sm leading-tight truncate text-foreground">{categoryLabel(category, t)}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {count === 0 ? t("documents:folder.noDocuments") : t("documents:folder.docCount", { count })}
        </p>
      </div>
    </button>
  );
}

function FileGridCard({
  doc,
  onView,
  onDelete,
}: {
  doc: UnifiedDoc;
  onView: (doc: UnifiedDoc) => void;
  onDelete?: (doc: UnifiedDoc) => void;
}) {
  const { toast } = useToast();
  const { t } = useTranslation(["documents", "common"]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const downloadUrl = doc.source === "parsed_statement"
    ? `/api/documents/${doc.id}/download`
    : doc.source === "bank_statement"
      ? `/api/bank-statements/${doc.id}/download`
      : doc.source === "financial_statement"
        ? null
        : `/api/dealer-documents/${doc.id}/download`;

  const iconStyle = getCategoryIconStyle(doc.category);
  const fileUnavailable = doc.fileExists === false;
  const isFinancialStatement = doc.source === "financial_statement";

  return (
    <div
      className="group relative flex flex-col rounded-xl border border-border bg-card hover:border-primary/25 hover:shadow-md transition-all duration-150 overflow-hidden"
      data-testid={`card-document-${doc.id}`}
    >
      <div className={`flex items-center justify-center py-6 ${iconStyle.folderBg}`}>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconStyle.bg}`}>
          <FileText className={`w-6 h-6 ${iconStyle.icon}`} />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 p-3 flex-1">
        <p
          className="text-sm font-medium leading-snug line-clamp-2 text-foreground"
          title={doc.fileName}
          data-testid={`text-filename-${doc.id}`}
        >
          {doc.fileName}
        </p>
        {doc.lenderName && (
          <p className="text-xs text-muted-foreground truncate">{doc.lenderName}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-1 gap-1">
          <p className="text-xs text-muted-foreground">{formatDate(doc.uploadedAt)}</p>
          {fileUnavailable ? (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive/80 font-medium shrink-0" data-testid={`badge-unavailable-${doc.id}`}>
              <FileX className="w-2.5 h-2.5" />
              {t("documents:card.fileUnavailable")}
            </span>
          ) : (
            <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium shrink-0 truncate max-w-[6rem]">
              {categoryLabel(doc.category, t)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-0.5 px-2 pb-2 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => onView(doc)}
          title={t("documents:card.viewDetails")}
          data-testid={`button-view-${doc.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          disabled={isGeneratingPdf || (isFinancialStatement ? false : (!doc.filePath || !downloadUrl || fileUnavailable))}
          onClick={async () => {
            if (isFinancialStatement) {
              setIsGeneratingPdf(true);
              try { await downloadFinancialStatementPdf(doc.id, toast, t); } finally { setIsGeneratingPdf(false); }
              return;
            }
            if (downloadUrl && !fileUnavailable) downloadDocument(downloadUrl, doc.fileName, toast, t);
          }}
          title={isGeneratingPdf ? t("documents:card.generatingPdf") : fileUnavailable ? t("documents:card.fileNoLongerAvailable") : t("documents:card.download")}
          data-testid={`button-download-${doc.id}`}
        >
          {isGeneratingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        </Button>
        {onDelete && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(doc)}
            title={t("documents:card.delete")}
            data-testid={`button-delete-${doc.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function DocumentListRow({
  doc,
  onView,
  onDelete,
}: {
  doc: UnifiedDoc;
  onView: (doc: UnifiedDoc) => void;
  onDelete?: (doc: UnifiedDoc) => void;
}) {
  const { toast } = useToast();
  const { t } = useTranslation(["documents", "common"]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const downloadUrl = doc.source === "parsed_statement"
    ? `/api/documents/${doc.id}/download`
    : doc.source === "bank_statement"
      ? `/api/bank-statements/${doc.id}/download`
      : doc.source === "financial_statement"
        ? null
        : `/api/dealer-documents/${doc.id}/download`;

  const iconStyle = getCategoryIconStyle(doc.category);
  const fileUnavailable = doc.fileExists === false;
  const isFinancialStatement = doc.source === "financial_statement";

  return (
    <div
      className="group flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors duration-100 border-b border-border/50 last:border-0"
      data-testid={`card-document-${doc.id}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconStyle.bg}`}>
        <FileText className={`w-4 h-4 ${iconStyle.icon}`} />
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <p className="font-medium text-sm truncate leading-tight flex-1 min-w-0" data-testid={`text-filename-${doc.id}`}>{doc.fileName}</p>
        {fileUnavailable ? (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 bg-destructive/10 text-destructive/80" data-testid={`badge-unavailable-${doc.id}`}>
            <FileX className="w-3 h-3" />
            {t("documents:card.fileUnavailable")}
          </span>
        ) : (
          <span className={`inline-flex text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${getCategoryColor(doc.category)}`}>
            {categoryLabel(doc.category, t)}
          </span>
        )}
        {doc.lenderName && (
          <span className="text-xs text-muted-foreground font-medium shrink-0 hidden sm:inline">{doc.lenderName}</span>
        )}
      </div>
      <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">{formatDate(doc.uploadedAt)}</span>
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-100">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => onView(doc)}
          title={t("documents:card.viewDetails")}
          data-testid={`button-view-${doc.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          disabled={isGeneratingPdf || (isFinancialStatement ? false : (!doc.filePath || !downloadUrl || fileUnavailable))}
          onClick={async () => {
            if (isFinancialStatement) {
              setIsGeneratingPdf(true);
              try { await downloadFinancialStatementPdf(doc.id, toast, t); } finally { setIsGeneratingPdf(false); }
              return;
            }
            if (downloadUrl && !fileUnavailable) downloadDocument(downloadUrl, doc.fileName, toast, t);
          }}
          title={isGeneratingPdf ? t("documents:card.generatingPdf") : fileUnavailable ? t("documents:card.fileNoLongerAvailable") : t("documents:card.download")}
          data-testid={`button-download-${doc.id}`}
        >
          {isGeneratingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        </Button>
        {onDelete && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(doc)}
            title={t("documents:card.delete")}
            data-testid={`button-delete-${doc.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const { data: documents, isLoading, isError } = useQuery<UnifiedDoc[]>({
    queryKey: ["/api/documents"],
  });
  const { data: currentUser } = useQuery<{ id: string } | null>({ queryKey: ["/api/auth/me"] });

  const { toast } = useToast();
  const { t } = useTranslation(["documents", "common"]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedDoc, setSelectedDoc] = useState<UnifiedDoc | null>(null);
  const [viewMode, setViewMode] = useState<"grouped" | "flat">("grouped");
  const [openedFolder, setOpenedFolder] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"date-desc" | "date-asc" | "name-asc" | "name-desc" | "size-desc" | "size-asc">("date-desc");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [taxExtractionInProgress, setTaxExtractionInProgress] = useState(false);
  const [taxExtractionJobId, setTaxExtractionJobId] = useState<string | null>(null);
  const taxPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const taxTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleDismissTaxBanner() {
    const taxDismissedJobKey = currentUser?.id ? `taxReturnDismissedJob:${currentUser.id}` : null;
    if (taxDismissedJobKey && taxExtractionJobId) {
      try { localStorage.setItem(taxDismissedJobKey, taxExtractionJobId); } catch {}
    }
    if (taxPollRef.current) { clearInterval(taxPollRef.current); taxPollRef.current = null; }
    if (taxTimeoutRef.current) { clearTimeout(taxTimeoutRef.current); taxTimeoutRef.current = null; }
    setTaxExtractionInProgress(false);
    setTaxExtractionJobId(null);
  }

  useEffect(() => {
    const taxJobStorageKey = currentUser?.id ? `taxReturnJobId:${currentUser.id}` : null;
    const taxDismissedJobKey = currentUser?.id ? `taxReturnDismissedJob:${currentUser.id}` : null;

    async function checkForActiveJob() {
      let resumeJobId: string | null = null;
      if (taxJobStorageKey) {
        try { resumeJobId = localStorage.getItem(taxJobStorageKey); } catch {}
      }

      if (!resumeJobId) {
        try {
          const res = await fetch("/api/parse-tax-return/active", { credentials: "include" });
          if (res.ok) {
            const data = await res.json();
            if (data?.jobId) resumeJobId = data.jobId;
          }
        } catch {}
      }

      if (!resumeJobId) return;

      if (taxDismissedJobKey) {
        try {
          const dismissedId = localStorage.getItem(taxDismissedJobKey);
          if (dismissedId && dismissedId === resumeJobId) return;
        } catch {}
      }

      try {
        const statusRes = await fetch(`/api/parse-tax-return/status/${resumeJobId}`, { credentials: "include" });
        if (!statusRes.ok) {
          if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
          return;
        }
        const statusJson = await statusRes.json();

        if (statusJson.status === "processing" || statusJson.status === "pending") {
          setTaxExtractionInProgress(true);
          setTaxExtractionJobId(resumeJobId);

          const TAX_POLL_TIMEOUT_MS = 8 * 60 * 1000;
          let taxLastProgressToken: string | null = null;

          const startTaxDeadline = () => {
            if (taxTimeoutRef.current) clearTimeout(taxTimeoutRef.current);
            taxTimeoutRef.current = setTimeout(() => {
              if (taxPollRef.current) { clearInterval(taxPollRef.current); taxPollRef.current = null; }
              taxTimeoutRef.current = null;
              if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
              setTaxExtractionInProgress(false);
              toast({ title: t("documents:toast.taxExtractionTimedOut"), description: t("documents:toast.extractionTakingLonger"), variant: "destructive" });
            }, TAX_POLL_TIMEOUT_MS);
          };
          startTaxDeadline();

          taxPollRef.current = setInterval(async () => {
            try {
              const pollRes = await fetch(`/api/parse-tax-return/status/${resumeJobId}`, { credentials: "include" });
              if (!pollRes.ok) {
                if (taxPollRef.current) { clearInterval(taxPollRef.current); taxPollRef.current = null; }
                if (taxTimeoutRef.current) { clearTimeout(taxTimeoutRef.current); taxTimeoutRef.current = null; }
                if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
                setTaxExtractionInProgress(false);
                return;
              }
              const pollJson = await pollRes.json();
              if (pollJson.status === "processing" || pollJson.status === "pending") {
                const prog = pollJson.progress as { page?: number; totalPages?: number } | null;
                const token = `${prog?.page ?? ""}|${prog?.totalPages ?? ""}`;
                if (token !== taxLastProgressToken) {
                  taxLastProgressToken = token;
                  startTaxDeadline();
                }
              } else {
                if (taxPollRef.current) { clearInterval(taxPollRef.current); taxPollRef.current = null; }
                if (taxTimeoutRef.current) { clearTimeout(taxTimeoutRef.current); taxTimeoutRef.current = null; }
                if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
                setTaxExtractionInProgress(false);
                if (pollJson.status === "done") {
                  toast({ title: t("documents:toast.taxReturnExtracted"), description: t("documents:toast.taxReturnExtractedDescription"), duration: 6000 });
                } else if (pollJson.status === "error") {
                  toast({ title: t("documents:toast.taxExtractionFailed"), description: pollJson.error || t("documents:toast.taxExtractionFailedDescription"), variant: "destructive" });
                }
              }
            } catch {
              if (taxPollRef.current) { clearInterval(taxPollRef.current); taxPollRef.current = null; }
              if (taxTimeoutRef.current) { clearTimeout(taxTimeoutRef.current); taxTimeoutRef.current = null; }
              setTaxExtractionInProgress(false);
            }
          }, 3000);
        } else {
          if (taxJobStorageKey) { try { localStorage.removeItem(taxJobStorageKey); } catch {} }
        }
      } catch {}
    }

    checkForActiveJob();

    return () => {
      if (taxPollRef.current) { clearInterval(taxPollRef.current); taxPollRef.current = null; }
      if (taxTimeoutRef.current) { clearTimeout(taxTimeoutRef.current); taxTimeoutRef.current = null; }
    };
  }, [currentUser?.id]);

  const [docToDelete, setDocToDelete] = useState<UnifiedDoc | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (doc: UnifiedDoc) => {
      const endpoint =
        doc.source === "parsed_statement"      ? `/api/parsed-statements/${doc.id}`
        : doc.source === "bank_statement"      ? `/api/bank-statements/${doc.id}`
        : doc.source === "financial_statement" ? `/api/financial-statements/${doc.id}`
        : `/api/dealer-documents/${doc.id}`;
      await apiRequest("DELETE", endpoint);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/parsed-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/bank-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/financial-statements"] });
      toast({ title: t("documents:toast.documentDeleted") });
      setDocToDelete(null);
    },
    onError: (err: Error) => {
      toast({ title: t("documents:toast.deleteFailed"), description: err.message, variant: "destructive" });
      setDocToDelete(null);
    },
  });

  const filtered = useMemo(() => {
    if (!documents) return [];
    return documents.filter((doc) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        doc.fileName.toLowerCase().includes(q) ||
        (doc.lenderName ?? "").toLowerCase().includes(q) ||
        (doc.description ?? "").toLowerCase().includes(q) ||
        (doc.tags ?? []).some((tag) => tag.toLowerCase().includes(q));

      const matchCategory = categoryFilter === "all" || doc.category === categoryFilter;

      let matchDate = true;
      if (startDate && doc.uploadedAt) {
        matchDate = matchDate && new Date(doc.uploadedAt) >= new Date(startDate);
      }
      if (endDate && doc.uploadedAt) {
        matchDate = matchDate && new Date(doc.uploadedAt) <= new Date(endDate + "T23:59:59");
      }

      return matchSearch && matchCategory && matchDate;
    });
  }, [documents, search, categoryFilter, startDate, endDate]);

  const countByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of DOCUMENT_CATEGORIES) counts[cat] = 0;
    for (const doc of filtered) {
      if (counts[doc.category] !== undefined) counts[doc.category]++;
      else counts[doc.category] = 1;
    }
    return counts;
  }, [filtered]);

  const sortComparator = useCallback((a: UnifiedDoc, b: UnifiedDoc): number => {
    switch (sortBy) {
      case "date-desc": return new Date(b.uploadedAt ?? 0).getTime() - new Date(a.uploadedAt ?? 0).getTime();
      case "date-asc":  return new Date(a.uploadedAt ?? 0).getTime() - new Date(b.uploadedAt ?? 0).getTime();
      case "name-asc":  return a.fileName.localeCompare(b.fileName, undefined, { sensitivity: "base" });
      case "name-desc": return b.fileName.localeCompare(a.fileName, undefined, { sensitivity: "base" });
      case "size-desc": return (b.fileSize ?? 0) - (a.fileSize ?? 0);
      case "size-asc":  return (a.fileSize ?? 0) - (b.fileSize ?? 0);
      default: return 0;
    }
  }, [sortBy]);

  const grouped = useMemo(() => {
    const g: Record<string, UnifiedDoc[]> = {};
    for (const cat of DOCUMENT_CATEGORIES) g[cat] = [];
    for (const doc of filtered) {
      if (g[doc.category]) g[doc.category].push(doc);
      else g[doc.category] = [doc];
    }
    for (const cat of Object.keys(g)) g[cat].sort(sortComparator);
    return g;
  }, [filtered, sortComparator]);

  const handleDelete = useCallback((doc: UnifiedDoc) => {
    setDocToDelete(doc);
  }, []);

  const flatSorted = useMemo(() => {
    return [...filtered].sort(sortComparator);
  }, [filtered, sortComparator]);

  const hasFilters = search || categoryFilter !== "all" || startDate || endDate;

  const visibleCategories = categoryFilter === "all" ? DOCUMENT_CATEGORIES : [categoryFilter as DocumentCategory];

  const folderDocs = openedFolder ? (grouped[openedFolder] ?? []) : [];

  return (
    <div className="flex h-full">
      <div className="w-48 shrink-0 border-r bg-sidebar px-3 py-4 overflow-y-auto flex flex-col gap-5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 px-1">{t("documents:filters.categories")}</p>
          <button
            onClick={() => { setCategoryFilter("all"); setOpenedFolder(null); }}
            className={`w-full flex items-center justify-between text-xs px-2 py-1.5 rounded-md transition-colors ${categoryFilter === "all" ? "border-l-2 border-primary bg-primary/5 text-primary font-semibold" : "hover:bg-muted text-foreground"}`}
            data-testid="filter-category-all"
          >
            <span>{t("documents:filters.allDocuments")}</span>
            <span className={`text-[10px] shrink-0 ${categoryFilter === "all" ? "text-primary/70" : "text-muted-foreground"}`}>{documents?.length ?? 0}</span>
          </button>
          <div className="mt-0.5 space-y-px">
            {DOCUMENT_CATEGORIES.map((cat) => {
              const count = (documents ?? []).filter((d) => d.category === cat).length;
              const active = categoryFilter === cat;
              const dotStyle = getCategoryIconStyle(cat).dot;
              return (
                <button
                  key={cat}
                  onClick={() => { setCategoryFilter(active ? "all" : cat); setOpenedFolder(active ? null : cat); }}
                  className={`w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded-md transition-colors ${active ? "border-l-2 border-primary bg-primary/5 text-primary font-semibold" : "hover:bg-muted text-foreground"}`}
                  data-testid={`filter-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotStyle}`} />
                  <span className="text-left leading-tight flex-1 truncate">{categoryLabel(cat, t)}</span>
                  <span className={`text-[10px] shrink-0 ${active ? "text-primary/70" : "text-muted-foreground"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 px-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {t("documents:filters.dateRange")}
          </p>
          <div className="space-y-1.5">
            <Input
              type="date"
              className="text-xs h-7"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="input-date-start"
            />
            <Input
              type="date"
              className="text-xs h-7"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="input-date-end"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 px-1"
                data-testid="button-clear-dates"
              >
                <X className="w-3 h-3" /> {t("documents:filters.clearDates")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-5xl space-y-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold" data-testid="heading-documents">{t("documents:header.title")}</h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {t("documents:header.subtitle")}
              </p>
            </div>
            <Button size="sm" onClick={() => setUploadDialogOpen(true)} data-testid="button-upload-document">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {t("documents:header.uploadDocument")}
            </Button>
          </div>

          {taxExtractionInProgress && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-primary/30 bg-primary/5 text-sm" data-testid="banner-tax-extraction-progress">
              <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />
              <span className="flex-1 text-foreground/80">
                {t("documents:taxBanner.runningPrefix")}<a href="/operations" className="text-primary hover:underline font-medium">{t("documents:taxBanner.runningLink")}</a>{t("documents:taxBanner.runningSuffix")}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                onClick={handleDismissTaxBanner}
                title={t("documents:taxBanner.dismiss")}
                data-testid="button-dismiss-tax-banner"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder={t("documents:search.placeholder")}
                className="pl-8 h-9 text-sm"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setOpenedFolder(null); }}
                data-testid="input-search-documents"
              />
            </div>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="h-8 w-40 text-xs gap-1.5" data-testid="select-sort-documents">
                <ArrowUpDown className="w-3 h-3 text-muted-foreground shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="date-desc">{t("documents:sort.dateDesc")}</SelectItem>
                <SelectItem value="date-asc">{t("documents:sort.dateAsc")}</SelectItem>
                <SelectItem value="name-asc">{t("documents:sort.nameAsc")}</SelectItem>
                <SelectItem value="name-desc">{t("documents:sort.nameDesc")}</SelectItem>
                <SelectItem value="size-desc">{t("documents:sort.sizeDesc")}</SelectItem>
                <SelectItem value="size-asc">{t("documents:sort.sizeAsc")}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center bg-muted rounded-lg p-0.5">
              <button
                onClick={() => { setViewMode("grouped"); setOpenedFolder(null); }}
                className={`p-1.5 rounded-md transition-colors ${viewMode === "grouped" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="button-view-grouped"
                title={t("documents:sort.folderView")}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { setViewMode("flat"); setOpenedFolder(null); }}
                className={`p-1.5 rounded-md transition-colors ${viewMode === "flat" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="button-view-flat"
                title={t("documents:sort.listView")}
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {isError ? (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-4" data-testid="error-state-documents">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
              <div>
                <p className="font-semibold text-lg">{t("documents:states.failedToLoad")}</p>
                <p className="text-muted-foreground text-sm mt-1">{t("documents:states.failedToLoadHint")}</p>
              </div>
            </div>
          ) : isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="skeleton-documents">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
            </div>
          ) : filtered.length === 0 && documents && documents.length > 0 && hasFilters && !openedFolder && !(viewMode === "grouped" && search.trim()) ? (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-4" data-testid="empty-state-filtered">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-semibold text-lg">{t("documents:states.noMatchingDocuments")}</p>
                <p className="text-muted-foreground text-sm mt-1">{t("documents:states.noMatchingDocumentsHint")}</p>
              </div>
            </div>
          ) : documents && documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center gap-4" data-testid="empty-state-documents">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <FolderOpen className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-semibold text-lg">{t("documents:states.noDocumentsYet")}</p>
                <p className="text-muted-foreground text-sm mt-1 max-w-xs">{t("documents:states.noDocumentsYetHint")}</p>
              </div>
              <Button onClick={() => setUploadDialogOpen(true)} data-testid="button-upload-empty-state">
                <Plus className="w-4 h-4 mr-2" />
                {t("documents:states.uploadFirstDocument")}
              </Button>
            </div>
          ) : viewMode === "grouped" ? (
            openedFolder ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <button
                    onClick={() => setOpenedFolder(null)}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="button-back-to-folders"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t("documents:states.allFolders")}
                  </button>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="font-medium text-foreground">{categoryLabel(openedFolder, t)}</span>
                </div>

                {folderDocs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center gap-3" data-testid={`empty-folder-${openedFolder}`}>
                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                      <FolderOpen className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold">{t("documents:states.noDocumentsInFolder")}</p>
                      <p className="text-muted-foreground text-sm mt-1">{t("documents:states.noDocumentsInFolderHint", { category: categoryLabel(openedFolder, t) })}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setUploadDialogOpen(true)} data-testid="button-upload-document-folder">
                      <Plus className="w-3.5 h-3.5 mr-1.5" />
                      {t("documents:header.uploadDocument")}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {folderDocs.map((doc) => (
                      <FileGridCard key={doc.id} doc={doc} onView={setSelectedDoc} onDelete={handleDelete} />
                    ))}
                  </div>
                )}
              </div>
            ) : search.trim() ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground flex-1">
                    {t("documents:search.resultsFor")}<span className="font-semibold text-foreground">"{search.trim()}"</span>
                  </p>
                  <Badge variant="secondary" className="shrink-0" data-testid="badge-search-result-count">
                    {t("documents:search.resultCount", { count: filtered.length })}
                  </Badge>
                </div>
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center gap-3" data-testid="empty-state-search-results">
                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                      <Search className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold">{t("documents:search.noDocumentsFound")}</p>
                      <p className="text-muted-foreground text-sm mt-1">{t("documents:search.noDocumentsFoundHint")}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="grid-search-results">
                    {flatSorted.map((doc) => (
                      <FileGridCard key={doc.id} doc={doc} onView={setSelectedDoc} onDelete={handleDelete} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {visibleCategories.map((cat) => (
                  <FolderTile
                    key={cat}
                    category={cat}
                    count={countByCategory[cat] ?? 0}
                    onClick={() => setOpenedFolder(cat)}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="rounded-xl border overflow-hidden">
              {flatSorted.map((doc) => (
                <DocumentListRow key={doc.id} doc={doc} onView={setSelectedDoc} onDelete={handleDelete} />
              ))}
            </div>
          )}

          {documents && documents.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="text-document-count">
              {t("documents:footer.showing", { filtered: filtered.length, count: documents.length })}
            </p>
          )}
        </div>
      </div>

      <DocumentDetailPanel
        document={selectedDoc}
        open={!!selectedDoc}
        onClose={() => setSelectedDoc(null)}
      />

      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => setUploadDialogOpen(false)}
      />

      <AlertDialog open={!!docToDelete} onOpenChange={(open) => { if (!open) setDocToDelete(null); }}>
        <AlertDialogContent data-testid="dialog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("documents:deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{docToDelete?.fileName}</span>{t("documents:deleteDialog.descriptionSuffix")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">{t("documents:deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (docToDelete) deleteMutation.mutate(docToDelete); }}
              disabled={deleteMutation.isPending}
              data-testid="button-delete-confirm"
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t("documents:deleteDialog.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
